import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Avatar, Paper, Rating, CircularProgress, Pagination, Button } from '@mui/material';
import moment from 'moment';
import 'moment/locale/es';
import { urlImg } from '../../common/helpers';
import { getPageReviews } from '../../../actions/reviews';

moment.locale('es');

const POR_PAGINA = 5;

// MUI pone en inglés las etiquetas de accesibilidad del paginador; aquí van en español.
const etiquetaPaginador = (tipo, pagina, seleccionada) => {
  if (tipo === 'page') return seleccionada ? `Página ${pagina}` : `Ir a la página ${pagina}`;
  if (tipo === 'first') return 'Primera página';
  if (tipo === 'last') return 'Última página';
  if (tipo === 'next') return 'Página siguiente';
  if (tipo === 'previous') return 'Página anterior';
  return undefined;
};

// Comentarios de una página (grupo, negocio o independiente): solo los de los servicios que esa página
// tiene vinculados, de 5 en 5.
const PageComments = ({ paginaId }) => {
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState(null); // última respuesta: { reviews, total, page, pages }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [intento, setIntento] = useState(0);
  const [paginaAnterior, setPaginaAnterior] = useState(paginaId);
  const seccion = useRef(null);

  // Al pasar a otra página se empieza de cero: sin comentarios ni número de hoja de la anterior
  if (paginaId !== paginaAnterior) {
    setPaginaAnterior(paginaId);
    setPagina(1);
    setDatos(null);
    setError(false);
  }

  useEffect(() => {
    if (!paginaId) return undefined;
    let vigente = true; // si se pide otra hoja antes de que llegue esta, su respuesta se descarta
    setLoading(true);
    setError(false);
    getPageReviews(paginaId, pagina, POR_PAGINA).then((res) => {
      if (!vigente) return;
      if (res) setDatos(res); else setError(true);
      setLoading(false);
    });
    return () => { vigente = false; };
  }, [paginaId, pagina, intento]);

  const cambiarPagina = (_, nueva) => {
    setPagina(nueva);
    // Si el encabezado de los comentarios quedó arriba de la pantalla, se vuelve a él
    const el = seccion.current;
    if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const reviews = datos ? datos.reviews : [];
  const total = datos ? datos.total : 0;
  const cargandoInicial = !datos && !error && Boolean(paginaId);

  return (
    <Box ref={seccion} sx={{ scrollMarginTop: '96px' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
        Comentarios destacados
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {total > 0
          ? `${total} ${total === 1 ? 'comentario' : 'comentarios'} sobre los servicios de esta página`
          : 'Comentarios sobre los servicios de esta página'}
      </Typography>

      {cargandoInicial ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={28} sx={{ color: 'secondary.main' }} />
        </Box>
      ) : error ? (
        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', bgcolor: 'grey.50', border: 1, borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No se pudieron cargar los comentarios.
          </Typography>
          <Button size="small" onClick={() => setIntento((n) => n + 1)} sx={{ textTransform: 'none' }}>
            Reintentar
          </Button>
        </Paper>
      ) : reviews.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', bgcolor: 'grey.50', border: 1, borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Aún no hay comentarios en los servicios de esta página.
          </Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
            {reviews.map((review) => {
              const cliente = review.cliente || {};
              const nombre = cliente.name ? `${cliente.name} ${cliente.apellido || ''}`.trim() : 'Usuario';
              const avatarSrc = cliente.avatar
                ? `${urlImg}/users/${cliente._id}/${cliente.avatar}?v=${cliente.versionAvatar || 0}`
                : null;

              return (
                <Paper key={review._id} elevation={0} sx={{ p: 2, mb: 2, borderRadius: '16px', bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Avatar src={avatarSrc} sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.8rem' }}>
                        {!avatarSrc && nombre.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>{nombre}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          hace {moment(review.createdAt).fromNow(true)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>{review.rating}</Typography>
                      <Rating value={review.rating} readOnly size="small" sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    </Box>
                  </Box>
                  {review.comentario && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                      {review.comentario}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Box>

          {datos.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
              <Pagination
                count={datos.pages}
                page={datos.page}
                onChange={cambiarPagina}
                color="primary"
                shape="rounded"
                size="small"
                getItemAriaLabel={etiquetaPaginador}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PageComments;
